import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useRef } from 'react';
import type { PersonOverviewProps } from './PersonOverview.data';
import styles from './PersonOverview.module.scss';
import { setupTransitionInTimeline } from './PersonOverview.transitions';
import { useScrollTransition } from 'util/react-transition-component';
import PersonDetails from '../person-details/PersonDetails';

import DynamicComponent from '../../DynamicComponent';
import Text from '../../atoms/text/Text';
import type { TextRef } from '../../atoms/text/Text.data';
import { useHistoryContext } from '../../../context/historyContext';
import Link from 'next/link';
import Script from 'next/script';
import { useRouter } from 'next/router';

export default function PersonOverview({
  canTransitionIn,
  className,
  blok,
  ref,
}: PersonOverviewProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const backToRef = useRef<TextRef>(null);
  const router = useRouter();
  // const locationRef = useRef<PrimaryLinkRef>(null);

  const imageHolderRef = useRef<HTMLDivElement>(null);

  useScrollTransition({
    scroll: {
      canTransitionIn,
      trigger: elementRef,
    },
    setupOptions: () => ({
      refs: {
        elementRef,
        imageHolderRef,
      },
      setupTransitionInTimeline,
    }),
  });

  const { history } = useHistoryContext();

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${process.env.SITE_URL}`,
      },
      // /people is not a page
      {
        '@type': 'ListItem',
        position: 2,
        name: blok.seo_title,
        item: `${process.env.SITE_URL}/${router.asPath}`,
      },
    ],
  };

  return (
    <div
      ref={elementRef}
      className={classNames(styles.personOverview, className)}
      {...sbEditable(blok)}
    >
      {history.length >= 2 && (
        <div className={styles.backToContainer}>
          <Text
            as="p"
            size="body-standard"
            disableTriggerInOnScroll={false}
            transitionInType="splitLineRotateIn"
            ref={backToRef}
          >
            <Link href={history[history.length - 2]} passHref>
              {}
              {blok.label_back_to}
            </Link>
          </Text>
          {/* <PrimaryLink */}
          {/*   ref={locationRef} */}
          {/*   textVariant="button-label-large" */}
          {/*   link={{ href: history[history.length - 2] }} */}
          {/*   // label={location.label} */}
          {/*   label="Location" */}
          {/*   disableTriggerInOnScroll={false} */}
          {/*   transitionInType="splitLineRotateIn" */}
          {/* /> */}
        </div>
      )}
      {blok?.person_details?.[0] && (
        <PersonDetails blok={blok.person_details[0]} className={classNames(styles.personDetails)} />
      )}
      {blok.sections?.map((module) => (
        <DynamicComponent key={module._uid} blok={module} canTransitionIn={canTransitionIn} />
      ))}
      <Script
        id="breadcrumbs-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema),
        }}
      />
    </div>
  );
}
